/**
 * AP-36 Phase C — **die Speicherstelle der Zeiterfassungs-Konfiguration.**
 *
 * `TenantTimeConfig` ist eine Zeile je Mandant, und mehrere Masken schreiben sie:
 * Grundeinstellungen, Datenbezug, Zusatzfelder, Auswahlwerte, Monatsbericht,
 * Scan-Regeln, QR-Pflicht. Sie teilen sich deshalb **eine** Stelle mit dem
 * Schlüssel `timeConfig` (AP-36 7.2) — sonst schickten zwei Masken derselben
 * Seite parallel mit demselben Stand und erzeugten sich gegenseitig 409.
 *
 * Die Standardfelder sind eine Spalte derselben Zeile, werden aber über einen
 * eigenen Endpunkt geschrieben (`PATCH /time-config/standard-fields`, er hält
 * gesperrte Felder fest). `schreibe` leitet sie dorthin und liest danach die
 * Konfiguration frisch — die Stelle kennt nur die eine Form des Admin-GET.
 *
 * Nur für Admin-Masken: `GET /time-config` ist `time.config.manage`
 * (`.claude/rules/konfiguration-lesen.md`).
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'

export const ZEIT_KONFIG_SCHLUESSEL = 'timeConfig'

async function schreibe(patch: Datensatz, stand: string | undefined): Promise<Datensatz> {
  const { standardFieldsConfig, ...rest } = patch
  let antwort: Datensatz | undefined
  if (Object.keys(rest).length > 0) {
    antwort = (await api.patch<Datensatz>('/time-config', rest, mitStand(stand))).data
  }
  if (standardFieldsConfig !== undefined) {
    const standJetzt = (antwort?.updatedAt as string | undefined) ?? stand
    await api.patch('/time-config/standard-fields', { fields: standardFieldsConfig }, mitStand(standJetzt))
    antwort = (await api.get<Datensatz>('/time-config')).data
  }
  return antwort ?? (await api.get<Datensatz>('/time-config')).data
}

/** Die Stelle; `daten` ist das Admin-GET (`GET /time-config`). */
export function useZeitKonfigStelle(daten: Datensatz | null): Speicherstelle<Datensatz> {
  return useSpeicherstelle<Datensatz>({
    schluessel: ZEIT_KONFIG_SCHLUESSEL,
    bezeichnung: 'Einstellungen der Zeiterfassung',
    daten,
    schreibe,
    keepalive: { url: '/time-config', method: 'PATCH' },
  })
}

/** Lädt die Konfiguration einmal und liefert die Stelle dazu. */
export function useZeitKonfiguration() {
  const [daten, setDaten] = useState<Datensatz | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    let aktiv = true
    api
      .get<Datensatz>('/time-config')
      .then((r) => aktiv && setDaten(r.data))
      .catch((e) => aktiv && setFehler(apiErrorText(e, 'Die Einstellungen konnten nicht geladen werden.')))
    return () => {
      aktiv = false
    }
  }, [])
  const stelle = useZeitKonfigStelle(daten)
  return { stelle, geladen: daten !== null, fehler, neuLaden: () => api.get<Datensatz>('/time-config').then((r) => stelle.uebernimm(r.data)) }
}
